import './theme.less'

import {DOMAttrs, Icon, m} from '@codeurs/front'
import {FunctionComponent, HTMLProps} from 'react'
import {NavLink} from 'react-router-dom'
import {createTheme} from 'util/createtheme'
import {Mod, style} from 'util/style'
import {Trusted} from 'util/trusted'

const styles = style('theme', [
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'container',
  'wysiwyg',
  'link',
  'button',
  'buttontext',
  'buttonicon',
  'buttoninlineicon'
])

type P = FunctionComponent<HTMLProps<HTMLParagraphElement>>

const P: P = props => <p {...props} class={styles.p.mergeProps(props)} />

type Header = FunctionComponent<HTMLProps<HTMLHeadingElement>>

export const H1: Header = ({as: Tag = 'h1', ...props}) => (
  <h1 {...props} className={styles.h1.mergeProps(props)} />
)
const H2: Header = ({as: Tag = 'h2', ...props}) => (
  <h2 {...props} className={styles.h2.mergeProps(props)} />
)
const H3: Header = ({as: Tag = 'h3', ...props}) => (
  <h3 {...props} className={styles.h3.mergeProps(props)} />
)
const H4: Header = ({as: Tag = 'h4', ...props}) => (
  <h4 {...props} className={styles.h4.mergeProps(props)} />
)
const H5: Header = ({as: Tag = 'h5', ...props}) => (
  <h5 {...props} className={styles.h5.mergeProps(props)} />
)

type Div = FunctionComponent<HTMLProps<HTMLDivElement>>

const Container: Div = props => (
  <div {...props} className={styles.container.mergeProps(props)} />
)

const Wysiwyg: FunctionComponent<
  {
    children: string
  } & DOMAttrs
> = props => {
  return (
    <div {...props} class={styles.wysiwyg.mergeProps(props)}>
      <Trusted children={props.children} />
    </div>
  )
}

type ButtonEl = HTMLProps<HTMLButtonElement>

type ButtonProps =
  | ({as: 'button'} & ButtonEl)
  | React.ComponentProps<typeof NavLink>
  | HTMLProps<HTMLAnchorElement>

type Button = ButtonProps & {
  mod?: Mod<'outline' | 'rounded' | 'round' | 'small' | 'primary'>
}

const Button: FunctionComponent<Button> = props => {
  const isExternal: boolean =
    'to' in props &&
    typeof props.to === 'string' &&
    (props.to.includes('://') || props.to.indexOf('/admin/storage') === 0)
  let linkprops = {}
  if (isExternal && 'to' in props) {
    linkprops = {
      target: props.target || '_blank',
      rel: 'external nofollow noopener',
      href: props.to
    }
  }
  const Tag: any =
    'as' in props
      ? props.as
      : 'to' in props
        ? isExternal
          ? 'a'
          : NavLink
        : 'a'
  const {mod} = props

  return (
    <Tag
      {...props}
      {...linkprops}
      class={styles.button.mergeProps(props).mod(mod)}
    />
  )
}
export const PrimaryButton: FunctionComponent<Button> = props => {
  const {mod} = props

  return (
    <Button
      {...props}
      className={styles.button
        .mergeProps(props)
        .mod('primary')
        .mod(mod)
        .toString()}
    />
  )
}

type Span = HTMLProps<HTMLSpanElement>

const ButtonText: FunctionComponent<Span> = props => (
  <span {...props} class={styles.buttontext.mergeProps(props)} />
)

const ButtonIcon: FunctionComponent<{icon: string} & Span> = ({
  icon,
  ...props
}) => (
  <span {...props} class={styles.buttonicon.mergeProps(props)}>
    <Icon icon={icon} />
  </span>
)
const ButtonInlineIcon: FunctionComponent<Span> = ({...props}) => (
  <span {...props} class={styles.buttoninlineicon.mergeProps(props)}>
    {props.children}
  </span>
)

const Link: FunctionComponent<React.ComponentProps<typeof NavLink>> = ({
  children,
  to,
  target,
  ...rest
}) => {
  const isExternal: boolean =
    typeof to === 'string' &&
    (to.includes('://') || to.indexOf('/admin/storage') === 0)
  if (typeof to === 'string' && !isExternal) {
    to = to.replace(/^\/en\//g, '/')
  }
  const Tag: any = isExternal ? 'a' : NavLink
  const props = isExternal
    ? {href: to, rel: 'external nofollow noopener', target: target || '_blank'}
    : {to, activeClassName: 'is-active', target}
  return (
    <Tag {...props} {...rest} class={styles.link.mergeProps(rest)}>
      {children}
    </Tag>
  )
}

export const defaultTheme = {
  P,
  H1,
  H2,
  H3,
  H4,
  H5,
  Container,
  Wysiwyg,
  Button,
  ButtonText,
  ButtonIcon,
  ButtonInlineIcon,
  PrimaryButton,
  Link
}

export const Theme = createTheme(defaultTheme)
